import { ParsedUrlQuery } from 'querystring';
import { UrlObject } from 'url';
import classNames from 'classnames';

import { MappedFilter } from '@components/SearchController/SearchController.utils';
import InputRange from '@templates/InputRange';
import ExpandArrow from '@components/atoms/ExpandArrow';
import Collapsible from '@components/Collapsible';
import { useIntl } from 'utils/intl';

import FilterTree from '../FilterTree';
import classes from './FilterBox.module.scss';
import messages from './FilterBox.messages';

type Props = {
  title: string;
  values: MappedFilter['values'];
  nodes?: MappedFilter[];
  initialValues: ParsedUrlQuery;
  checkedNodes: string[];
  mainLevelNodesLimit: number;
  isNested?: boolean;
  isRange?: boolean;
  getFilterChangeHref: (value: string[], key: string) => UrlObject;
  onSubmit: () => void;
};

const FilterBox = ({
  title,
  values,
  nodes,
  initialValues,
  checkedNodes,
  mainLevelNodesLimit,
  isNested = false,
  isRange = false,
  getFilterChangeHref,
  onSubmit,
}: Props): JSX.Element => {
  const intl = useIntl();

  const suffixMap: Record<string, string> = {
    price: intl.formatMessage(messages.plnSuffix),
  };

  const searchKey = values?.[0].searchKey;

  return (
    <>
      {isNested ? (
        <Collapsible
          initiallyOpen={!!checkedNodes.length}
          trigger={(isOpened, handleClick) => (
            <div
              className={classNames(classes.nestedTitle, { [classes.openedTitle]: isOpened || !!checkedNodes.length })}
              onClick={handleClick}
            >
              {title}
              <ExpandArrow isOpened={isOpened} />
            </div>
          )}
        >
          {values && (
            <div className={classes.values}>
              <FilterTree
                nodes={values}
                checkedNodes={checkedNodes}
                mainLevelNodesLimit={mainLevelNodesLimit}
                getFilterChangeHref={getFilterChangeHref}
              />
            </div>
          )}
        </Collapsible>
      ) : (
        <div>
          {title && (
            <div className={classes.title} data-eid="filter-title">
              {title}
            </div>
          )}
          {values && !isRange && (
            <FilterTree
              nodes={values}
              checkedNodes={checkedNodes}
              mainLevelNodesLimit={mainLevelNodesLimit}
              getFilterChangeHref={getFilterChangeHref}
            />
          )}
          {values && isRange && (
            <InputRange
              name={searchKey}
              suffix={suffixMap[searchKey]}
              firstValue={`${initialValues[`${searchKey}_from`] || ''}`}
              secondValue={`${initialValues[`${searchKey}_to`] || ''}`}
              firstPlaceholder={intl.formatMessage(messages.from)}
              secondPlaceholder={intl.formatMessage(messages.to)}
              isFirstWrong={false}
              isSecondWrong={false}
              classes={{ root: classes.inputRange }}
              onChange={onSubmit}
            />
          )}
        </div>
      )}
      {!!nodes?.length && (
        <div className={classes.nestedNodes}>
          {nodes.map(({ key, values: nestedValues, nodes: nestedNodes, name: nestedName, isRange: nestedIsRange }) => (
            <FilterBox
              key={key}
              title={nestedName}
              values={nestedValues}
              nodes={nestedNodes}
              isRange={nestedIsRange}
              initialValues={initialValues}
              checkedNodes={[initialValues[key] || []].flat()}
              mainLevelNodesLimit={mainLevelNodesLimit}
              getFilterChangeHref={getFilterChangeHref}
              onSubmit={onSubmit}
              isNested
            />
          ))}
        </div>
      )}
    </>
  );
};

export default FilterBox;
